/**
 * Owner: Masa Bayu
 * File: install-mode.ts
 * Date:Time: 2026-09-22 06:02
 * Description: PWA-Phase-A Plane I — install/display mode · Install ≠ unlock.
 */

import type { PwaCapabilitySnapshot } from './capability';

export type PwaInstallMode =
  'browser' | 'promptable' | 'installing' | 'installed' | 'standalone' | 'blocked' | 'a2hs_guided';

export interface PwaInstallModeView {
  readonly mode: PwaInstallMode;
  readonly statusLabel: string;
  readonly helperText: string;
  readonly primaryAction: 'install' | 'a2hs' | 'none';
  readonly unlocksContent: false;
  readonly grantsEntitlement: false;
}

function isLikelyIosSafari(userAgent: string): boolean {
  const ua = userAgent.toLowerCase();
  const ios = /iphone|ipad|ipod/.test(ua);
  const safari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
  return ios && safari;
}

export function resolvePwaInstallMode(input: {
  readonly capability: PwaCapabilitySnapshot;
  readonly userAgent?: string;
  readonly softInstalledHint?: boolean;
  readonly installing?: boolean;
}): PwaInstallModeView {
  const ua = input.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');

  if (!input.capability.secureContext || !input.capability.serviceWorker) {
    return Object.freeze({
      mode: 'blocked',
      statusLabel: 'Install needs a secure connection and browser support',
      helperText: 'You can keep using QXRead in the browser. Install does not unlock books.',
      primaryAction: 'none',
      unlocksContent: false,
      grantsEntitlement: false,
    });
  }

  if (input.capability.standalone) {
    return Object.freeze({
      mode: 'standalone',
      statusLabel: 'Running as installed app',
      helperText: 'App shell only — access still comes from your library entitlement.',
      primaryAction: 'none',
      unlocksContent: false,
      grantsEntitlement: false,
    });
  }

  /* §400 — selepas pengguna menerima dialog, pelayan muat turun aplikasi
     secara senyap; mod ini memaparkan penunjuk "sedang dipasang" sehingga
     peristiwa appinstalled (atau timeout keselamatan) tiba. */
  if (input.installing === true) {
    return Object.freeze({
      mode: 'installing',
      statusLabel: 'Installing app…',
      helperText: 'Look for the QX icon on your home screen. Install does not unlock books.',
      primaryAction: 'none',
      unlocksContent: false,
      grantsEntitlement: false,
    });
  }

  if (input.softInstalledHint === true) {
    return Object.freeze({
      mode: 'installed',
      statusLabel: 'Installed on this device',
      helperText:
        'Open from your home screen for the full-screen shell. Does not unlock publications.',
      primaryAction: 'none',
      unlocksContent: false,
      grantsEntitlement: false,
    });
  }

  if (input.capability.beforeInstallPrompt) {
    return Object.freeze({
      mode: 'promptable',
      statusLabel: 'Available to install',
      helperText: 'Install for quicker access. Does not unlock books.',
      primaryAction: 'install',
      unlocksContent: false,
      grantsEntitlement: false,
    });
  }

  if (isLikelyIosSafari(ua)) {
    return Object.freeze({
      mode: 'a2hs_guided',
      statusLabel: 'Add to Home Screen',
      helperText: 'On iPhone/iPad: Share → Add to Home Screen. This does not unlock publications.',
      primaryAction: 'a2hs',
      unlocksContent: false,
      grantsEntitlement: false,
    });
  }

  return Object.freeze({
    mode: 'browser',
    statusLabel: 'Using browser',
    helperText: 'Install may appear from your browser menu when available. Does not unlock books.',
    primaryAction: 'none',
    unlocksContent: false,
    grantsEntitlement: false,
  });
}
